import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTrAnswer, AppTrSettings, AppTrSuggestion, AppTranslate } from "./AppTranslate";
import { AppTaskTips } from "./AppTaskTips";
import { AppHistoryRow } from "./AppSendHistory";
import { AppLiveRules } from "./AppLiveRules";

// AppTranslateRules - THE APP'S HALF OF TRANSLATION, as rules (`documentation/design/translation.md`, owner
// 2026-09-27). The wire and the file shapes are `AppTranslate` (the shared contract, pinned against the service);
// this file decides what the PAGE does with them: the chip's words, the suggestion, whether a
// send carries `translate: true`, when "Translate earlier messages" is offered, and which failed rows still offer
// Retry / Send in English.
//
// ⚠ RULES ONLY - no DOM, no `window`, no transport (the Bridge suite may import it beside `AppTranslate`).
//
// ⛔ A SEND IS TRANSLATED ONLY WHEN THIS VISIT KNOWS THE CHIP IS ON FOR THAT CHAT (`AppTrBook.Wants`): "yes" and
// "no" are answers from the service; a chat not asked yet is "ask" - the page asks `/translate get` BEFORE the request
// is written. ⛔ A LOST answer (status 0 / 5xx) for a chat LAST SEEN ON (`IAppTrMemory`) refuses the send - English by
// surprise is the one failure the design forbids; for any other chat it sends as typed (coordinator 2026-09-27:
// sending must never regress because of a dormant feature).

/** One chat's translation state as this visit knows it. */
export class AppTrChat {
    /** The service answered `get` for this chat (Ok or a refusal) - `Active`/`Available` are its answer. */
    public Known: boolean = false;
    /** The last ask did not reach the service (status 0 / 5xx) - NOT an answer. */
    public Lost: boolean = false;
    public Available: boolean = false;
    public Settings: AppTrSettings | null = null;
    public Suggested: AppTrSuggestion | null = null;
    public Capped: boolean = false;
    public Unreadable: boolean = false;
    /** A refusal sentence from the service ("" when it said yes). */
    public Why: string = "";
    /** ⭐ The last answer was a refusal that clears on its own: ask again after this many seconds (0 = none). */
    public RetrySec: number = 0;
}

/**
 * ⭐ WHICH CHATS WERE LAST SEEN WITH TRANSLATION ON - kept across visits (the page's `localStorage`, `AppTrLocalMemory`).
 * It decides only what a LOST `get` means (coordinator ruling 2026-09-27): a chat last seen ON refuses the send (never
 * English by surprise); any other chat sends as typed (sending never regresses because of a dormant feature).
 */
export interface IAppTrMemory {
    WasOn(Href: string): boolean;
    Note(Href: string, On: boolean): void;
}

/** The memory of one visit only - the suite's, and the fallback where storage is refused. */
export class AppTrVisitMemory implements IAppTrMemory {
    private On: Record<string, boolean> = {};
    /** DOCUMENTED EXCEPTION: a lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public WasOn = (Href: string): boolean => { return this.On[nz(Href)] === true; };
    /** DOCUMENTED EXCEPTION: a write. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Note = (Href: string, On: boolean): void => { this.On[nz(Href)] = On; };
}

/**
 * What this visit knows about translation, per page href. ⭐ `Off` is visit-wide: a service without the route
 * (dormant, older) answers the status document once, and nothing is asked again in this visit.
 */
export class AppTrBook {
    public Off: boolean = false;
    private Chats: Record<string, AppTrChat> = {};
    public Memory: IAppTrMemory = new AppTrVisitMemory();

    /** Read one `get` (or `set`) answer for `Href`. `Status` 0 / >= 500 is a lost connection, never an answer. */
    public Take = (Href: string, Status: number, Text: string): AppTrChat => {
        if (getCoreUtils().KErrorMode === true) { return new AppTrChat(); }
        try {
            const Chat: AppTrChat = this.Of(Href);
            if (Status === 0 || Status >= 500) { Chat.Lost = true; return Chat; }
            const Answer: AppTrAnswer = AppTranslate.Parse(Text);
            if (Answer.Off === true) {
                this.Off = true; Chat.Lost = false; Chat.Known = true; Chat.Available = false;
                this.Memory.Note(Href, false);
                return Chat;
            }
            AppTrBook.Apply(Chat, Answer);
            if (Answer.Ok === true) { this.Memory.Note(Href, Chat.Available === true && AppTranslate.Active(Chat.Settings) === true); }
            return Chat;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrBook.Take");
            return new AppTrChat();
        }
    };

    /** DOCUMENTED EXCEPTION: copies parsed fields onto the state. ERROR-SUPPRESSED-JUSTIFIED: pure; `Take` is guarded. */
    private static Apply = (Chat: AppTrChat, Answer: AppTrAnswer): void => {
        Chat.Why = Answer.Why;
        Chat.RetrySec = 0;
        // ⛔ A REFUSAL THAT CLEARS ON ITS OWN IS NOT AN ANSWER FOR THE VISIT (measured live 2026-09-28: every contact
        // answered "not in your archive yet" for minutes after each restart, and the chip stayed hidden until a reload).
        // It reads like a lost connection - a chat last seen ON is asked again before a send, never sent in English by
        // surprise - and the chip asks again after `RetrySec`.
        if (Answer.Ok !== true && Answer.RetrySec > 0) {
            Chat.Known = false;
            Chat.Lost = true;
            Chat.RetrySec = Answer.RetrySec;
            return;
        }
        Chat.Lost = false;
        Chat.Known = true;
        if (Answer.Ok !== true) { return; }
        // A `set` answer carries no `available` - it could only have been saved where translation is available.
        Chat.Settings = Answer.Settings;
        Chat.Capped = Answer.Capped;
        if (Answer.Saved === true) { Chat.Available = true; Chat.Unreadable = false; return; }
        Chat.Available = Answer.Available;
        Chat.Suggested = Answer.Suggested;
        Chat.Unreadable = Answer.Unreadable;
    };

    /** The state for `Href` (made empty on first use). */
    public Of = (Href: string): AppTrChat => {
        if (getCoreUtils().KErrorMode === true) { return new AppTrChat(); }
        try {
            const Key: string = nz(Href);
            if (Object.prototype.hasOwnProperty.call(this.Chats, Key) === false) { this.Chats[Key] = new AppTrChat(); }
            return this.Chats[Key];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrBook.Of");
            return new AppTrChat();
        }
    };

    /** Another tab changed this chat's settings: forget them, so the next look asks again. */
    public Forget = (Href: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            delete this.Chats[nz(Href)];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrBook.Forget");
        }
    };

    /**
     * ⭐ DOES A SEND TO THIS CHAT CARRY `translate: true`? "yes", "no", or "ask" (the service has not answered in
     * this visit - ask before writing the request). ⛔ See the header: never "no" for a chat merely not known.
     */
    public Wants = (Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "ask"; }
        try {
            if (this.Off === true) { return "no"; }
            const Chat: AppTrChat = this.Of(Href);
            // ⛔ A LOST ANSWER: a chat last seen ON must ask again (and is refused if that is lost too); any other chat
            // sends as typed - sending never regresses because translation could not be checked.
            if (Chat.Known !== true && Chat.Lost === true && this.Memory.WasOn(Href) === false) { return "no"; }
            if (Chat.Known !== true) { return "ask"; }
            if (Chat.Available !== true) { return "no"; }
            if (AppTranslate.Active(Chat.Settings) === true) { return "yes"; }
            return "no";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrBook.Wants");
            return "ask";
        }
    };
}

export class AppTranslateRules {
    /** "⇄" - made from its code point (the edit tooling has turned literal glyphs into other characters before). */
    public static readonly Arrows: string = String.fromCharCode(0x21C4);
    /** The chip's word when translation is not running for this chat. */
    public static readonly ChipOff: string = "Translate";
    /** A send waited for the chip's state and the service could not be reached. Nothing was written. */
    public static readonly LostWhy: string = "Couldn't check whether this chat is translated - nothing was sent."
        + " Check your connection and press Send again.";
    /** A `set` / `earlier` that did not reach the service (or a service without the route). */
    public static readonly SaveLostWhy: string = "Couldn't reach WACommander to save that - check your connection and try again.";
    public static readonly EarlierLostWhy: string = "Couldn't translate earlier messages just now - try again in a moment.";
    public static readonly ChipTitleOn: string = "Translation settings for this chat";
    /** The hint under the box when a translated send could not be translated (the bubble carries the buttons). */
    /**
     * ⚠ It names no button: the second one is named after the OWNER'S language (`PlainLabel`), which this line - said
     * by the composer, outside the frame - does not know.
     */
    public static readonly FailedHint: string = "Not sent - it couldn't be translated. Use the buttons on the message to try again or to send your own words.";
    public static readonly RetryLabel: string = "Retry";
    /** The second button when the owner's language is English or not known yet (the manual's 5.8 picture). */
    public static readonly EnglishLabel: string = "Send in English";
    /** The second button for a language the picker has no name for - true whatever was typed. */
    public static readonly TypedLabel: string = "Send as typed";

    /**
     * ⭐ THE FAILED BUBBLE'S SECOND BUTTON SAYS THE OWNER'S OWN LANGUAGE (audit 2026-10-12). It sends the words exactly
     * as typed, and it read "Send in English" for everybody - false for an owner who reads and writes Swedish. "Send in
     * English" for `en` and while the language is not known yet, "Send in Swedish" for `sv`, "Send as typed" for a
     * language with no name here.
     */
    public static PlainLabel = (MyLang: string): string => {
        if (getCoreUtils().KErrorMode === true) { return AppTranslateRules.EnglishLabel; }
        try {
            const Code: string = nz(MyLang).trim();
            if (Code.length === 0) { return AppTranslateRules.EnglishLabel; }
            if (AppTranslate.LangOk(Code) === false) { return AppTranslateRules.TypedLabel; }
            const Name: string = AppTranslate.NameOf(Code);
            if (Name === Code) { return AppTranslateRules.TypedLabel; }
            return "Send in " + Name;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.PlainLabel");
            return AppTranslateRules.EnglishLabel;
        }
    };
    public static readonly EarlierLabel: string = "Translate earlier messages";

    /** "EN ⇄ ID" while translation runs for this chat, else "Translate". */
    public static ChipLabel = (S: AppTrSettings | null): string => {
        if (getCoreUtils().KErrorMode === true) { return AppTranslateRules.ChipOff; }
        try {
            if (AppTranslate.Active(S) === false) { return AppTranslateRules.ChipOff; }
            const Set: AppTrSettings = S as AppTrSettings;
            return AppTranslate.Base(Set.MyLang).toUpperCase() + " " + AppTranslateRules.Arrows + " "
                + AppTranslate.Base(Set.TheirLang).toUpperCase();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.ChipLabel");
            return AppTranslateRules.ChipOff;
        }
    };

    /** The chip's tooltip - which languages, in words. */
    public static ChipTitle = (S: AppTrSettings | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTranslate.Active(S) === false) { return "Translate this chat"; }
            const Set: AppTrSettings = S as AppTrSettings;
            return "You read and write in " + AppTranslate.NameOf(Set.MyLang) + "; they get "
                + AppTranslate.NameOf(Set.TheirLang) + ". Tap to change.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.ChipTitle");
            return "";
        }
    };

    /**
     * ⭐ THE SUGGESTION (owner's words): "Mirliyana writes in Bahasa Indonesia - translate this chat?". "" when there
     * is nothing to suggest: no suggestion, translation already running, or the owner switched it OFF themselves
     * (a saved file with `on: false` is a decision - it is never re-offered).
     */
    public static SuggestLine = (Title: string, Chat: AppTrChat): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Chat.Available !== true || Chat.Suggested === null || Chat.Unreadable === true) { return ""; }
            if (Chat.Settings !== null && AppTranslateRules.Decided(Chat.Settings as AppTrSettings) === true) { return ""; }
            let Who: string = AppTaskTips.FirstName(Title);
            if (Who.length === 0) { Who = "This contact"; }
            return Who + " writes in " + AppTranslate.NameOf((Chat.Suggested as AppTrSuggestion).TheirLang)
                + " - translate this chat?";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.SuggestLine");
            return "";
        }
    };

    /** A settings file the owner saved (on, or deliberately off with a language chosen). DOCUMENTED EXCEPTION: pure.
     *  ERROR-SUPPRESSED-JUSTIFIED: field reads, cannot throw; every caller is guarded. */
    public static Decided = (S: AppTrSettings): boolean => {
        return S.On === true || S.TheirLang.length > 0 || S.At.length > 0;
    };

    /**
     * The settings the suggestion's one tap saves: its two languages, ON, the glossary kept. ⛔ Only ever called
     * from the owner's tap on "Translate" - the page never switches translation on by itself.
     */
    public static Accepted = (Chat: AppTrChat): AppTrSettings | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Chat.Suggested === null) { return null; }
            const Out: AppTrSettings = new AppTrSettings();
            Out.MyLang = (Chat.Suggested as AppTrSuggestion).MyLang;
            Out.TheirLang = (Chat.Suggested as AppTrSuggestion).TheirLang;
            Out.On = true;
            if (Chat.Settings !== null) { Out.Glossary = (Chat.Settings as AppTrSettings).Glossary.slice(); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.Accepted");
            return null;
        }
    };

    /** The panel's starting point: the saved settings, else the suggestion's languages (off), else blanks. */
    public static DraftOf = (Chat: AppTrChat): AppTrSettings => {
        const Out: AppTrSettings = new AppTrSettings();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (Chat.Settings !== null) {
                const S: AppTrSettings = Chat.Settings as AppTrSettings;
                Out.MyLang = S.MyLang; Out.TheirLang = S.TheirLang; Out.On = S.On; Out.Glossary = S.Glossary.slice();
                return Out;
            }
            if (Chat.Suggested !== null) {
                Out.MyLang = (Chat.Suggested as AppTrSuggestion).MyLang;
                Out.TheirLang = (Chat.Suggested as AppTrSuggestion).TheirLang;
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.DraftOf");
            return Out;
        }
    };

    /** Why the panel's settings cannot be saved yet, or "" (they can). Both languages when it is switched on. */
    public static SaveWhy = (S: AppTrSettings): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (S.On !== true) { return ""; }
            if (AppTranslate.LangOk(S.MyLang) === false || AppTranslate.LangOk(S.TheirLang) === false) {
                return "Pick both languages first.";
            }
            if (AppTranslate.SameLanguage(S.MyLang, S.TheirLang) === true) {
                return "Those are the same language - pick two different ones, or switch translation off.";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.SaveWhy");
            return "";
        }
    };


    // ── Translate earlier messages ─────────────────────────────────────────────────────────────

    /**
     * `before` for "Translate earlier messages": the time of the OLDEST translated bubble on the page (its id's
     * UTC minute), "" when none is translated yet (the service then starts from the newest - the backfill).
     */
    public static BeforeOf = (TranslatedMinutes: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Oldest: string = "";
            for (const One of TranslatedMinutes) {
                if (/^\d{12}$/.test(nz(One)) === false) { continue; }
                if (Oldest.length === 0 || One < Oldest) { Oldest = One; }
            }
            return AppLiveRules.FloorOf(Oldest);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.BeforeOf");
            return "";
        }
    };

    /**
     * Offer the link? Only while translation runs for this chat, and only when there is reason to: the service
     * said there is More, or the page has bubbles from the contact older than its oldest translated one.
     * DOCUMENTED EXCEPTION: pure boolean. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static OfferEarlier = (Active: boolean, More: boolean, OlderUntranslated: boolean): boolean => {
        return Active === true && (More === true || OlderUntranslated === true);
    };

    // ── Failed translated sends ───────────────────────────────────────────────────────────────

    /** A history row whose words could not be translated - NOTHING was sent. DOCUMENTED EXCEPTION: pure.
     *  ERROR-SUPPRESSED-JUSTIFIED: a field compare, cannot throw. */
    public static IsFailed = (Row: AppHistoryRow): boolean => {
        return Row.State === AppTranslate.FailedState;
    };

    /**
     * ⛔ A FAILED ROW OFFERS Retry / Send in English ONLY UNTIL THE OWNER ACTED: once a LATER row in the same chat
     * carries the same words (a Retry or a Send in English is a new request with the same text), the old row's
     * buttons would invite a second copy - so it no longer offers them.
     */
    public static Superseded = (Row: AppHistoryRow, All: AppHistoryRow[]): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            const At: number = Date.parse(Row.At);
            for (const Other of All) {
                if (Other.Id === Row.Id || Other.ChatId !== Row.ChatId || nz(Other.Text) !== nz(Row.Text)) { continue; }
                if (Date.parse(Other.At) >= At) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateRules.Superseded");
            return true;
        }
    };
}
